import { ResponsiveLine } from '@nivo/line'
import type { PartialTheme } from '@nivo/theming'
import { useNivoTheme } from '@/components/marketing/visualizations/nivoTheme'
import type { DashboardTrendPoint } from '@/types/dashboard-summary'
import { toConversationSeries, toRevenueSeries, type TrendSeries } from './trendSeries'

interface DashboardTrendChartProps {
  points: DashboardTrendPoint[]
  includeRevenue: boolean
  /** ISO currency code for the revenue axis label (e.g. "MXN"). */
  currency?: string
  /** Height of each panel. */
  height?: number
}

/** Compact axis tick for money: 13500 → "13.5k", 14000 → "14k". */
function formatAxisMoney(v: number): string {
  if (Math.abs(v) >= 1000) {
    const k = v / 1000
    return `${Number.isInteger(k) ? k : k.toFixed(1)}k`
  }
  return String(v)
}

function TrendPanel({
  title, series, points, theme, axisLegend, yFormat, height,
}: {
  title: string
  series: TrendSeries[]
  points: DashboardTrendPoint[]
  theme: PartialTheme
  axisLegend: string
  yFormat: (v: number) => string
  height: number
}) {
  return (
    <div>
      <h3 className="mb-1 text-xs font-medium text-muted-foreground">{title}</h3>
      <div style={{ height }}>
        <ResponsiveLine
          data={series}
          theme={theme}
          margin={{ top: 12, right: 20, bottom: 44, left: 64 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, max: 'auto', stacked: false }}
          curve="monotoneX"
          colors={series.map((s) => s.color)}
          lineWidth={2}
          pointSize={points.length <= 14 ? 6 : 0}
          pointColor={{ from: 'color' }}
          pointBorderWidth={2}
          pointBorderColor={{ from: 'seriesColor' }}
          enableArea
          areaOpacity={0.08}
          enableGridX={false}
          enableSlices="x"
          crosshairType="bottom"
          axisBottom={{ tickRotation: points.length > 14 ? -45 : 0, tickSize: 5, tickPadding: 5 }}
          axisLeft={{
            tickSize: 5, tickPadding: 5, format: yFormat,
            legend: axisLegend, legendPosition: 'middle', legendOffset: -56,
          }}
          motionConfig="gentle"
        />
      </div>
    </div>
  )
}

/**
 * Conversations and revenue as separate, independently-scaled panels — a shared
 * axis would swamp the 10s–100s/day conversation counts under thousands/day of
 * revenue. Revenue is only shown when the caller may see it, with the currency
 * labelled on its axis.
 */
export function DashboardTrendChart({ points, includeRevenue, currency = 'USD', height = 220 }: DashboardTrendChartProps) {
  const theme = useNivoTheme()
  const hasConv = points.some((p) => p.conversations > 0)
  const hasRev = includeRevenue && points.some((p) => (p.revenueCents ?? 0) > 0)

  if (!hasConv && !hasRev) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        No activity in this period yet
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {includeRevenue && (
        <TrendPanel
          title={`Revenue (${currency})`}
          series={toRevenueSeries(points)}
          points={points}
          theme={theme}
          axisLegend={currency}
          yFormat={formatAxisMoney}
          height={height}
        />
      )}
      <TrendPanel
        title="Conversations"
        series={toConversationSeries(points)}
        points={points}
        theme={theme}
        axisLegend="conversations"
        yFormat={(v) => String(v)}
        height={height}
      />
    </div>
  )
}
